/**
 * One attribute row — the densest object in the cockpit.
 *
 * It carries, in one line: the key, the value, the value-vs-dictionary check (✓ / ≈ / ?),
 * where the value came from, whether it reached the generator, and what the dictionary
 * knows about the attribute. Everything else expands on click.
 *
 * The POC revealed this detail on HOVER. Here it is click-to-expand: hover-only content is
 * unreachable on touch and by keyboard, and this is the part a reviewer most needs to read.
 */
import React, { useState } from 'react';
import type {
  DictionaryEntry, EnrichedAttribute, SemanticGap,
} from '@/services/productCockpitService';
import { valueCheck, normVal, type ValueCheckState } from '../lib/checks';
import { DEFS } from '../lib/stages';
import { OriginChip } from './OriginChip';

const VC_GLYPH: Record<ValueCheckState, string> = { ok: '✓', variant: '≈', unvalidated: '?' };

function vcTip(state: ValueCheckState, known: number, match?: string): string {
  switch (state) {
    case 'ok':
      return `✓ value check — this value is one of the ${known} known values for the attribute.`;
    case 'variant':
      return `≈ value check — equals known value "${match}" up to case/format. A spelling or format variant: synonym handling makes it exact, then it turns green.`;
    default:
      return `? value check — NOT among the ${known} known values. Either a wrong value, a true synonym to manage, or a value the dictionary must learn — the semantic-gap research decides which.`;
  }
}

function displayValue(v: unknown): string {
  if (Array.isArray(v)) return v.join(', ');
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

interface AttrRowProps {
  attrKey: string;
  value: unknown;
  /** dictionary entry for this attribute, when the Interpret layer is on */
  entry?: DictionaryEntry | null;
  /** run the value check (Interpret step, learned attributes only) */
  check?: boolean;
  origin?: React.ReactNode;
  sourceQuote?: string | null;
  /** semantic gaps queued for THIS product's attributes */
  gaps?: SemanticGap[];
}

export function AttrRow({
  attrKey, value, entry, check = false, origin, sourceQuote, gaps = [],
}: AttrRowProps) {
  const [open, setOpen] = useState(false);
  const vc = check ? valueCheck(entry, value) : null;

  const badges: React.ReactNode[] = [];
  if (entry) {
    if (entry.is_key_attribute) badges.push(<span key="k" className="pmc-sbadge pmc-key" data-tip={DEFS.badge_key}>★</span>);
    if (entry.definition) badges.push(<span key="d" className="pmc-sbadge" data-tip={DEFS.badge_def}>def</span>);
    if (entry.enum_values?.length) badges.push(<span key="v" className="pmc-sbadge" data-tip={DEFS.badge_values}>values</span>);
    if (entry.extraction_hints) badges.push(<span key="h" className="pmc-sbadge" data-tip={DEFS.badge_hints}>hints</span>);
    if (entry.typical_min != null || entry.typical_max != null) badges.push(<span key="r" className="pmc-sbadge" data-tip={DEFS.badge_range}>range</span>);
  }

  // the gap queued for this exact (attribute, value) pair — the actionable link
  const ownGaps = gaps.filter((g) => g.attribute_name === attrKey);
  const exactGap = ownGaps.find((g) => normVal(g.technology_value) === normVal(value));

  const hasDetail = !!(
    vc || sourceQuote || (entry && (entry.definition || entry.enum_values?.length || entry.extraction_hints))
    || (check && !entry)
  );

  return (
    <div className={`pmc-srow${entry ? ' pmc-has-dict' : ''}${hasDetail ? ' pmc-expandable' : ''}`}>
      <div
        className="pmc-srow-main"
        {...(hasDetail
          ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-expanded': open,
            onClick: () => setOpen((o) => !o),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
            },
          }
          : {})}
      >
        <span className="pmc-sk">{attrKey}</span>
        <span className="pmc-sval">{displayValue(value)}</span>
        {vc && (
          <span
            className={`pmc-sbadge pmc-vcheck pmc-vc-${vc.state}`}
            data-tip={vcTip(vc.state, vc.known, vc.match)}
          >
            {VC_GLYPH[vc.state]}
          </span>
        )}
        {origin}
        {badges.length > 0 && <span className="pmc-sbadges">{badges}</span>}
        {hasDetail && <span className="pmc-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>}
      </div>

      {open && hasDetail && (
        <div className="pmc-tipbox">
          {entry?.definition
            ? <div className="pmc-tiprow"><b>definition </b>{entry.definition}</div>
            : entry && (
              <div className="pmc-tiprow">
                <span className="pmc-syn">
                  {entry.extraction_hints
                    ? '⚠ incoherence: extraction hints exist but NO definition is recorded — the dictionary learned how to read this attribute, not what it means'
                    : 'no definition recorded for this attribute'}
                </span>
              </div>
            )}

          {entry && (entry.unit || entry.typical_min != null) && (
            <div className="pmc-tiprow">
              <b>range </b>{`${entry.typical_min ?? '?'} – ${entry.typical_max ?? '?'} ${entry.unit ?? ''}`}
            </div>
          )}

          {!!entry?.enum_values?.length && (
            <div className="pmc-tiprow">
              <b>known values </b>
              {entry.enum_values.slice(0, 12).map((v) => String(v)).join(' · ')}
              {entry.enum_values.length > 12 && (
                <span className="pmc-mute"> … {entry.enum_values.length - 12} more</span>
              )}
            </div>
          )}

          {entry?.extraction_hints && (
            <div className="pmc-tiprow"><b>extraction hint </b>{entry.extraction_hints}</div>
          )}

          {check && !entry && (
            <div className="pmc-tiprow">
              <span className="pmc-syn">
                not in the category dictionary — the semantic layer cannot explain this
                attribute (no definition, no known values, no hints possible)
              </span>
            </div>
          )}

          {vc && (
            <div className="pmc-tiprow">
              <b>value check </b>
              <span className="pmc-syn">{vcTip(vc.state, vc.known, vc.match)}</span>
            </div>
          )}

          {vc && vc.state !== 'ok' && (exactGap
            ? (
              <div className="pmc-tiprow">
                <b>semantic gap </b>
                {`a gap for THIS value is queued — status ${exactGap.status}, since ${String(exactGap.created_at ?? '').slice(0, 10)}`}
                {exactGap.suggested_semantic_value ? ` · suggested: "${exactGap.suggested_semantic_value}"` : ''}
                {'. Research validates it, a human approves, and the value turns green.'}
              </div>
            )
            : ownGaps.length > 0 && (
              <div className="pmc-tiprow">
                <b>semantic gap </b>
                {`research is queued on this attribute (${ownGaps[0].status}, ${String(ownGaps[0].created_at ?? '').slice(0, 10)}) — not yet for this exact value.`}
              </div>
            ))}

          {sourceQuote && (
            <div className="pmc-tiprow"><b>seen in source </b>{`❝ ${sourceQuote} ❞`}</div>
          )}
        </div>
      )}
    </div>
  );
}

/** A learned attribute row: adds the origin chip and the "reached the generator" badge. */
export function LearnedAttrRow({
  attr, entry, check, gaps, feedField,
}: {
  attr: EnrichedAttribute;
  entry?: DictionaryEntry | null;
  check?: boolean;
  gaps?: SemanticGap[];
  feedField?: string | null;
}) {
  const reach = attr.reached_generator === true
    ? <span className="pmc-sbadge pmc-gen" data-tip="→ gen — this attribute WAS in the actual generation prompt (recorded from S3): the page writer read it.">→ gen</span>
    : attr.reached_generator === false
      ? <span className="pmc-sbadge pmc-gen pmc-off" data-tip="✗ gen — in the knowledge base today but NOT in the generation prompt: either added after generation, or never injected. The page was written without it.">✗ gen</span>
      : null;

  return (
    <AttrRow
      attrKey={attr.key}
      value={attr.value}
      entry={entry}
      check={check}
      gaps={gaps}
      sourceQuote={attr.source_quote}
      origin={(
        <span className="pmc-sorigin">
          <OriginChip origin={attr.origin} feedField={feedField} />
          {reach}
        </span>
      )}
    />
  );
}
